import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import type { SagaStepView, SagaStepStatus } from '../types/sagaMonitor';

interface SagaStepTimelineProps {
  steps: SagaStepView[];
}

/** 보상 발생 여부 — status 가 보상 관련이거나 compensatedAt 이 기록된 단계.
 * ★ BE 는 null 필드를 JSON 에서 생략(NON_NULL)하므로 compensatedAt 은 undefined 로 도착 —
 *   `!== null` 강비교는 undefined 를 통과시켜 FAILED(미보상) 단계가 보상 구간에 오포함된다. */
function wasCompensated(step: SagaStepView): boolean {
  return (
    step.status === 'COMPENSATED' ||
    step.status === 'COMPENSATING' ||
    step.status === 'COMPENSATION_FAILED' ||
    step.compensatedAt != null
  );
}

/**
 * forward(정방향) 단계 상태 — 단계는 인스턴스당 1 row 라 최종 status 가 보상으로 덮여
 * 있을 수 있다. forward 구간에는 "실행 시점" 결과(성공/실패/미도달)를 별도로 파생한다.
 */
function forwardStatus(step: SagaStepView): SagaStepStatus {
  if (step.status === 'FAILED') return 'FAILED';
  if (step.executedAt) return 'DONE';
  return 'PENDING';
}

function stepStatusTone(status: SagaStepStatus): StatusTone {
  switch (status) {
    case 'DONE': return 'success';
    case 'COMPENSATED': return 'info';
    case 'FAILED': return 'danger';
    case 'COMPENSATING': return 'warning';
    case 'COMPENSATION_FAILED': return 'danger';
    case 'PENDING': return 'neutral';
    default: return 'neutral';
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  });
}

/**
 * SagaStepTimeline — 보상 체인 시각화 컴포넌트
 * 정방향 단계(전체 단계, executedAt 기준 실행 결과) → 보상 발생 단계(역순, compensatedAt 기준) 시각화
 */
export function SagaStepTimeline({ steps }: SagaStepTimelineProps) {
  const { t } = useTranslation();

  if (steps.length === 0) {
    return (
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          padding: 'var(--spacing-4) 0',
        }}
      >
        {t('sagaMonitor.detail.steps.noSteps')}
      </p>
    );
  }

  const forwardSteps = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
  const compensationSteps = steps
    .filter(wasCompensated)
    .sort((a, b) => b.stepOrder - a.stepOrder);
  const hasCompensation = compensationSteps.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
      {/* 정방향 실행 구간 */}
      <div>
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 'var(--spacing-3)',
            margin: '0 0 var(--spacing-3)',
          }}
        >
          {t('sagaMonitor.timeline.forwardPhase')}
        </p>
        <div style={{ position: 'relative' }}>
          {/* 수직 연결선 */}
          {forwardSteps.length > 1 && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 11,
                top: 24,
                bottom: 24,
                width: 2,
                background: 'var(--color-surface-border)',
              }}
            />
          )}
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            {forwardSteps.map((step) => {
              const status = forwardStatus(step);
              return (
                <li
                  key={`${step.id}-forward`}
                  aria-label={t('sagaMonitor.timeline.stepAriaLabel', {
                    name: step.stepName,
                    status: t(`sagaMonitor.stepStatus.${status}`),
                  })}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)', position: 'relative' }}
                >
                  {/* 상태 아이콘 */}
                  <StepIcon status={status} />
                  {/* 내용 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        {step.stepName}
                      </span>
                      <StatusPill tone={stepStatusTone(status)}>
                        {t(`sagaMonitor.stepStatus.${status}`)}
                      </StatusPill>
                    </div>
                    {step.executedAt && (
                      <p
                        style={{
                          margin: 'var(--spacing-1) 0 0',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontFamily: 'var(--font-family-mono)',
                        }}
                      >
                        {formatDateTime(step.executedAt)}
                      </p>
                    )}
                    {status === 'FAILED' && step.errorMessage && (
                      <p
                        style={{
                          margin: 'var(--spacing-1) 0 0',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-danger)',
                          fontFamily: 'var(--font-family-mono)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {step.errorMessage}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {/* 보상 실행 구간 */}
      {hasCompensation && (
        <div>
          {/* 보상 구분선 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <div style={{ flex: 1, height: 1, background: 'var(--color-warning)' }} />
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--color-warning)',
                whiteSpace: 'nowrap',
                padding: '0 var(--spacing-2)',
              }}
            >
              ↩ {t('sagaMonitor.timeline.compensationPhase')}
            </span>
            <div style={{ flex: 1, height: 1, background: 'var(--color-warning)' }} />
          </div>
          <div style={{ position: 'relative' }}>
            {compensationSteps.length > 1 && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 11,
                  top: 24,
                  bottom: 24,
                  width: 2,
                  background: 'color-mix(in srgb, var(--color-warning) 40%, var(--color-surface-border))',
                }}
              />
            )}
            <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
              {compensationSteps.map((step) => (
                <li
                  key={`${step.id}-compensation`}
                  aria-label={t('sagaMonitor.timeline.stepAriaLabel', {
                    name: step.stepName,
                    status: t(`sagaMonitor.stepStatus.${step.status}`),
                  })}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)', position: 'relative' }}
                >
                  <StepIcon status={step.status} isCompensation />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          textDecoration: 'line-through',
                          textDecorationColor: 'var(--color-warning)',
                        }}
                      >
                        {step.stepName}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontStyle: 'italic',
                        }}
                      >
                        ({t('sagaMonitor.timeline.compensationTag')})
                      </span>
                      <StatusPill tone={stepStatusTone(step.status)}>
                        {t(`sagaMonitor.stepStatus.${step.status}`)}
                      </StatusPill>
                    </div>
                    {step.compensatedAt && (
                      <p
                        style={{
                          margin: 'var(--spacing-1) 0 0',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontFamily: 'var(--font-family-mono)',
                        }}
                      >
                        {formatDateTime(step.compensatedAt)}
                      </p>
                    )}
                    {step.status === 'COMPENSATION_FAILED' && step.errorMessage && (
                      <p
                        style={{
                          margin: 'var(--spacing-1) 0 0',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-danger)',
                          fontFamily: 'var(--font-family-mono)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {step.errorMessage}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

/** 단계 상태 아이콘 */
function StepIcon({ status, isCompensation = false }: { status: SagaStepStatus; isCompensation?: boolean }) {
  const iconMap: Record<SagaStepStatus, { symbol: string; bg: string; color: string }> = {
    DONE:               { symbol: '✓', bg: 'var(--color-success)', color: 'var(--color-text-inverse)' },
    COMPENSATED:        { symbol: '↩', bg: 'var(--color-info)', color: 'var(--color-text-inverse)' },
    FAILED:             { symbol: '✕', bg: 'var(--color-danger)', color: 'var(--color-text-inverse)' },
    COMPENSATING:       { symbol: '↺', bg: 'var(--color-warning)', color: 'var(--color-text-inverse)' },
    COMPENSATION_FAILED:{ symbol: '!', bg: 'var(--color-danger)', color: 'var(--color-text-inverse)' },
    PENDING:            { symbol: '○', bg: 'var(--color-surface-border)', color: 'var(--color-text-secondary)' },
  };

  const icon = iconMap[status] ?? iconMap.PENDING;
  const bg = isCompensation && status === 'COMPENSATED'
    ? 'var(--color-info)'
    : icon.bg;

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: bg,
        color: icon.color,
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        zIndex: 1,
        position: 'relative',
        fontFamily: 'var(--font-family-sans)',
      }}
    >
      {icon.symbol}
    </span>
  );
}
